import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import UIMedia from './media';
import UITitle from './title';
import UITagLine from './tag_line';
import UIDivider from './divider';
import SeoLink from '../common/seo_link';
import getAbsolutePositionCSS from '../dynamic_pages/section_types/common/absolute_position_css';
import getTranslucentLayerCSS from '../dynamic_pages/section_types/common/translucent_layer_css';
import { nullable } from '../../utils/prop_types';
import { getSizingCSS, normalizePolicy } from '../../element_sizing/css';
import { elementSizePolicyPropType } from '../../element_sizing/size_policy_type';
import { sourceImageSizeSpecPropType } from '../../element_sizing/source_image_size_spec_type';

const UIMediaAndContentCard = (props) => (
  <CardsWrapper reverse={props.reverse}>
    <MediaCard media={props.media} alt={props.mediaAlt} cta={props.mediaCta} breakpoints={props.breakpoints}
      breakpointFlags={props.breakpointFlags} tagLine={props.mediaTagLine} title={props.mediaTitle}
      titleColor={props.mediaTitleColor} titlePosition={props.mediaTitlePosition}
      translucentLayerColor={props.translucentLayerColor} includeTranslucentOverlay={props.includeTranslucentOverlay}
      translucentLayerOpacity={props.translucentLayerOpacity} mediaSizePolicy={props.mediaSizePolicy}
      sourceMediaSizeSpec={props.sourceMediaSizeSpec} />
    <ContentCardWrapper policy={props.mediaSizePolicy} noPadding={props.noPadding}>
      {props.children}
    </ContentCardWrapper>
  </CardsWrapper>);

if (process.env.NODE_ENV !== 'production') {
  UIMediaAndContentCard.propTypes = {
    reverse: PropTypes.bool,
    media: PropTypes.string.isRequired,
    mediaAlt: PropTypes.string.isRequired,
    mediaCta: PropTypes.shape({
      link: nullable(PropTypes.string.isRequired)
    }),
    breakpoints: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
    breakpointFlags: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    mediaTagLine: PropTypes.string,
    mediaTitle: PropTypes.string,
    mediaTitleColor: PropTypes.string,
    mediaTitlePosition: PropTypes.oneOf([
      'center-center', 'center-left', 'center-right', 'top-center', 'top-left', 'top-right',
      'bottom-center', 'bottom-left', 'bottom-right']),
    includeTranslucentOverlay: PropTypes.bool,
    translucentLayerColor: PropTypes.oneOf(['white', 'black']),
    translucentLayerOpacity: PropTypes.number,
    mediaSizePolicy: elementSizePolicyPropType.isRequired,
    sourceMediaSizeSpec: sourceImageSizeSpecPropType.isRequired
  };
}

UIMediaAndContentCard.defaultProps = {
  reverse: false,
  mediaCta: { link: null },
  mediaTagLine: null,
  mediaTitle: null,
  mediaTitleColor: '#fff',
  mediaTitlePosition: 'center-center',
  includeTranslucentOverlay: false,
  translucentLayerColor: 'black',
  translucentLayerOpacity: 0.125
};

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media screen and (min-width: 768px) {
    flex-direction: ${props => props.reverse ? 'row-reverse' : 'row'};
    justify-content: center;
    align-items: flex-start;
  }
`;

const MediaCard = (props) => {
  const contents = (
    <CardImageWrapper includeTranslucentOverlay={props.includeTranslucentOverlay}
      translucentLayerOpacity={props.translucentLayerOpacity} translucentLayerColor={props.translucentLayerColor}>
      <UIMedia mediaSizePolicy={props.mediaSizePolicy} sourceMediaSizeSpec={props.sourceMediaSizeSpec}
        breakpoints={props.breakpoints} breakpointFlags={props.breakpointFlags} alt={props.alt}
        media={props.media} />
      <TitleWrapper position={props.titlePosition}>
        <UITagLine textColor={props.titleColor}>{props.tagLine}</UITagLine>
        {props.title &&
          <UITitle as='h2' lines={props.title.split('\n')} noBottomMargin color={props.titleColor} fontWeight={900} />}
        {props.title && <UIDivider color={props.titleColor} noBottomMargin />}
      </TitleWrapper>
    </CardImageWrapper>
  );
  if (props.cta && props.cta.link) {
    return <SeoLink to={props.cta.link}>{contents}</SeoLink>;
  }
  return contents;
};

const CardImageWrapper = styled.div`
  position: relative;
  ${props => props.includeTranslucentOverlay &&
    getTranslucentLayerCSS(
      props.translucentLayerColor === 'black'
        ? `rgba(0, 0, 0, ${props.translucentLayerOpacity})`
        : `rgba(255, 255, 255, ${props.translucentLayerOpacity})`,
      3)};
`;

const TitleWrapper = styled.div`
  ${props => getAbsolutePositionCSS(props.position)};
  white-space: pre-wrap;
  user-select: none;
  z-index: 4;
`;

// This will make the element have an automatic height on mobile, no matter what.
const fixPolicyForContentCard = (policy) => {
  const normalizedPolicy = normalizePolicy(policy);
  const breakpointKeys = Object.keys(normalizedPolicy.heights);
  for (let i = 0; i < breakpointKeys.length; ++i) {
    const breakpointKey = breakpointKeys[i];
    if (breakpointKey === 'default' || parseInt(breakpointKey, 10) < 768) {
      normalizedPolicy.heights[breakpointKey] = 'auto';
    }
  }
  return normalizedPolicy;
};

const ContentCardWrapper = styled.div`
  background: #fff;
  width: 100%; height: auto;
  ${props => !props.noPadding && 'padding: 32px'};
  @media screen and (min-width: 768px) { padding: 24px; }
  @media screen and (min-width: 1200px) { padding: 48px; }
  ${props => getSizingCSS(fixPolicyForContentCard(props.policy))};
`;

export default UIMediaAndContentCard;
